import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";
import Loading from "components/atoms/loading/Loading";
import Toast from "components/atoms/toast/Toast";

import AccountPage from "components/page/AccountPage";
import DashboardPage from "components/page/DashboardPage";
import ErrorPage from "components/page/ErrorPage";
import LandingPage from "components/page/LandingPage";
import ManuscriptEditorPage from "components/page/ManuscriptEditorPage";
import LoginPage from "components/page/LoginPage";
import ManuscriptListPage from "components/page/ManuscriptListPage";
import NoteDetailPage from "components/page/NoteDetailPage";
import NoteListPage from "components/page/NoteListPage";
import PasswordResetPage from "components/page/PasswordResetPage";
import PlanPage from "components/page/PlanPage";
import PrivacyPolicyPage from "components/page/PrivacyPolicyPage";
import ReferenceDetailPage from "components/page/ReferenceDetailPage";
import ReferenceListPage from "components/page/ReferenceListPage";
import ReportPage from "components/page/ReportPage";
import SearchPage from "components/page/SearchPage";
import SettingsPage from "components/page/SettingsPage";
import SignUpPage from "components/page/SignUpPage";
import SpacePage from "components/page/SpacePage";
import TermsOfServicePage from "components/page/TermsOfServicePage";

import { useUser } from "hooks/queries/useUser";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";

import "App.scss";

const Container = styled.div`
    width: 100%;
    height: 100%;
    min-height: inherit;
    ${(props) =>
        props.$review &&
        `
    overflow: hidden;
    `}
`;

const Row = styled.div`
    display: flex;
    align-items: flex-start;
`;

function App() {
    const location = useLocation();
    const pathname = location.pathname;

    useSessionPageHistory();

    const ProtectedRoute = ({ children }) => {
        if (localStorage.getItem("token")) {
            return <Outlet />;
        } else {
            Alert("warn", "에러", "로그인이 필요합니다.");
            return <Navigate to={`/login?next=${encodeURIComponent(`${pathname}${location.search || ""}`)}`} replace />;
        }
    };

    const RootRoute = () => {
        const isTokenAuthed = !!localStorage.getItem("token");
        const userQuery = useUser({ enabled: isTokenAuthed });

        if (!isTokenAuthed) {
            return <LandingPage />;
        }

        if (userQuery.isLoading || userQuery.isIdle) {
            return null;
        }

        if (userQuery.data?.is_authenticated === true) {
            const privateSpaceUuid = userQuery.data?.private_space?.uuid;
            if (privateSpaceUuid) {
                return <Navigate to={`/${privateSpaceUuid}`} replace />;
            }
        }

        return <LandingPage />;
    };

    const SpaceRoute = () => {
        const userQuery = useUser();

        if (userQuery.isLoading || userQuery.isIdle || userQuery.isError) {
            return null;
        }

        if (userQuery.data) {
            const currentSpace = userQuery.data.current_space;
            const privateSpace = userQuery.data.private_space;

            if (!currentSpace && privateSpace?.uuid) {
                return <Navigate to={`/${privateSpace.uuid}`} replace />;
            }
        }

        return <Outlet />;
    };

    return (
        <div className="App">
            <Loading>
                <Toast />
                <Container $review={pathname.includes("/review")}>
                    <Row>
                        <Routes>
                            <Route path="/" element={<RootRoute />} />

                            <Route exact path={"/plan"} element={<PlanPage />} />
                            <Route exact path={"/privacy-policy"} element={<PrivacyPolicyPage />} />
                            <Route exact path={"/terms-of-service"} element={<TermsOfServicePage />} />

                            <Route exact path={"/login"} element={<LoginPage />} />
                            <Route exact path={"/signup"} element={<SignUpPage />} />
                            <Route exact path={"/reset-password"} element={<PasswordResetPage />} />

                            <Route exact path={"/error"} element={<ErrorPage />} />

                            {/* 로그인 필요 페이지 */}
                            <Route element={<ProtectedRoute />}>
                                {/* 스페이스와 무관한 페이지 */}
                                <Route exact path={"/account"} element={<AccountPage />} />
                                <Route exact path={"/settings"} element={<SettingsPage />} />

                                {/* 스페이스 하위 페이지 */}
                                <Route path={":space_uuid"} element={<SpaceRoute />}>
                                    <Route index element={<DashboardPage />} />

                                    <Route path={"search"} element={<SearchPage />} />
                                    <Route path={"report"} element={<ReportPage />} />
                                    <Route path={"note"}>
                                        <Route index element={<NoteListPage />} />
                                        <Route path={":note_no"} element={<NoteDetailPage />} />
                                    </Route>
                                    <Route path={"manuscript"}>
                                        <Route index element={<ManuscriptListPage />} />
                                        <Route path={":manuscriptId"} element={<ManuscriptEditorPage />} />
                                    </Route>
                                    <Route path={"reference"}>
                                        <Route index element={<ReferenceListPage />} />
                                        <Route path={":reference_no"} element={<ReferenceDetailPage />} />
                                    </Route>

                                    <Route path={"management"} element={<SpacePage />} />
                                </Route>
                            </Route>
                        </Routes>
                    </Row>
                </Container>
            </Loading>
        </div>
    );
}

export default App;
